import { Environment } from '~/types/Environments'
import { AuthRole } from '~/utils/auth'

export type RouteParamValue = string | string[] | null | undefined

const devMappings: Record<string, string> = {
  'animal-feed': '5b2e8c7e-29a3-45c6-8271-83c365cb2087',
  'international-expert-committee-animal-feed': 'fbfe126b-1b52-f111-8fcb-000d3abbb0b0',
  'subcommittee-certification-and-compliance': '200cdc2c-2152-f111-8fcb-000d3abbb0b0',
  'subcommittee-production': '230cdc2c-2152-f111-8fcb-000d3abbb0b0',
  'subcommittee-responsible-feed': '150cdc2c-2152-f111-8fcb-000d3abbb0b0',
  'subcommittee-trade-and-collection': '350cdc2c-2152-f111-8fcb-000d3abbb0b0',
  'subcommittee-transport': '380cdc2c-2152-f111-8fcb-000d3abbb0b0',
  'technical-committee-fsp': '010cdc2c-2152-f111-8fcb-000d3abbb0b0',
}

const testMappings: Record<string, string> = {
  'international-expert-committee-animal-feed': '5b18f010-9f5d-f111-8fcb-000d3abbb0b0',
  'subcommittee-certification-and-compliance': '6b590089-9f5d-f111-8fcb-000d3abbb0b0',
  'subcommittee-production': '68590089-9f5d-f111-8fcb-000d3abbb0b0',
  'subcommittee-responsible-feed': '73590089-9f5d-f111-8fcb-000d3abbb0b0',
  'subcommittee-trade-and-collection': '6d590089-9f5d-f111-8fcb-000d3abbb0b0',
  'subcommittee-transport': '6a590089-9f5d-f111-8fcb-000d3abbb0b0',
  'technical-committee-fsp': '66590089-9f5d-f111-8fcb-000d3abbb0b0',
}

export const internationalExpertCommitteeAnimalFeed = {
  slug: 'international-expert-committee-animal-feed',
  bannerColor: 'affirmative',
  title: 'International Expert Committee Animal Feed',
  navigationName: 'internationalExpertCommitteeAnimalFeed',
  roles: [AuthRole.PORTAL_IECAF],
} as const

export const subcommitteeCertificationAndCompliance = {
  slug: 'subcommittee-certification-and-compliance',
  bannerColor: 'blueish',
  title: 'Subcommittee Certification and Compliance',
  navigationName: 'subcommitteeCertificationAndCompliance',
  roles: [AuthRole.PORTAL_SCAC],
} as const

export const subcommitteeProduction = {
  slug: 'subcommittee-production',
  bannerColor: 'affirmative',
  title: 'Subcommittee Production',
  navigationName: 'subcommitteeProduction',
  roles: [AuthRole.PORTAL_SP],
} as const

export const subcommitteeResponsibleFeed = {
  slug: 'subcommittee-responsible-feed',
  bannerColor: 'primary',
  title: 'Subcommittee Responsible Feed',
  navigationName: 'subcommitteeResponsibleFeed',
  roles: [AuthRole.PORTAL_SRF],
} as const

export const subcommitteeTradeAndCollection = {
  slug: 'subcommittee-trade-and-collection',
  bannerColor: 'affirmative',
  title: 'Subcommittee Trade and Collection',
  navigationName: 'subcommitteeTradeAndCollection',
  roles: [AuthRole.PORTAL_STAC],
} as const

export const subcommitteeTransport = {
  slug: 'subcommittee-transport',
  bannerColor: 'affirmative',
  title: 'Subcommittee Transport',
  navigationName: 'subcommitteeTransport',
  roles: [AuthRole.PORTAL_ST],
} as const

export const technicalCommitteeRiskManagement = {
  slug: 'technical-committee-fsp',
  bannerColor: 'affirmative',
  title: 'Technical Committee Risk Management',
  navigationName: 'technicalCommitteeRiskManagement',
  roles: [AuthRole.PORTAL_TCFSP],
} as const

export const expertCommitteeRoutes = [
  internationalExpertCommitteeAnimalFeed,
  subcommitteeCertificationAndCompliance,
  subcommitteeProduction,
  subcommitteeResponsibleFeed,
  subcommitteeTradeAndCollection,
  subcommitteeTransport,
  technicalCommitteeRiskManagement,
] as const

export type ExpertCommitteeSlug = (typeof expertCommitteeRoutes)[number]['slug']

export const expertCommitteeNavigationRoles = [
  ...new Set(expertCommitteeRoutes.flatMap(({ roles }) => roles)),
]

const committeeRootCategoryIds: Record<string, Record<string, string>> = {
  [Environment.development]: devMappings,
  [Environment.local]: devMappings,
  [Environment.test]: testMappings,
  [Environment.acceptance]: testMappings,
  [Environment.production]: testMappings,
}

export const normalizeToArray = (param: RouteParamValue): string[] => {
  if (!param) {
    return []
  }

  return (Array.isArray(param) ? param : [param]).filter(Boolean)
}

export const getLastItem = <T>(arr: readonly T[]): T | undefined => arr.at(-1)

export const resolveCategoryIdFromSlug = (
  slug: string,
  env: string = Environment.development,
): string | undefined => {
  const mappings =
    committeeRootCategoryIds[env] || committeeRootCategoryIds[Environment.development]
  return mappings[slug] || devMappings[slug]
}

export const extractCategoryIds = (segments: RouteParamValue): string[] => {
  const normalizedSegments = normalizeToArray(segments)
  const articleSegmentIndex = normalizedSegments.indexOf('article')

  return articleSegmentIndex >= 0
    ? normalizedSegments.slice(0, articleSegmentIndex)
    : normalizedSegments
}

type CategoryBreadcrumb = {
  id: string
  title: string
}

export const getParentCategoryNavigation = (
  categoryRootPath: string,
  breadcrumbs: readonly CategoryBreadcrumb[],
): { label: string; path: string } | undefined => {
  if (breadcrumbs.length < 2) {
    return undefined
  }

  const parentIndex = breadcrumbs.length - 2
  const parent = breadcrumbs[parentIndex]
  const parentCategoryIds = breadcrumbs.slice(1, parentIndex + 1).map(({ id }) => id)

  return {
    label: parent.title,
    path:
      parentCategoryIds.length > 0
        ? `${categoryRootPath}/${parentCategoryIds.join('/')}`
        : categoryRootPath,
  }
}
